import type { Theme } from '@mui/material';
import {
  gridFilteredSortedRowIdsSelector,
  gridColumnFieldsSelector,
  gridVisibleColumnFieldsSelector,
  type GridApi,
  type GridCellParams,
} from '@mui/x-data-grid-pro';
import { saveAs } from 'file-saver';
import type { MutableRefObject } from 'react';
import type { Cell, SheetData } from 'write-excel-file/browser';

const EXCEL_FILE_NAME = 'sports_book_bo.xlsx';
const EXCEL_SHEET_NAME = 'Sheet1';
const EXCEL_DATE_FORMAT = 'mm/dd/yyyy';
const EXCLUDED_COLUMN_FIELDS = new Set(['edit', 'actions']);

type ExportCellValue = string | number | boolean | Date | null;

export type ExcelExportData = {
  headers: string[];
  rows: ExportCellValue[][];
};

const camelCaseToTitleCase = (value: string): string =>
  value.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (character) => character.toUpperCase());

const isExportCellValue = (value: unknown): value is Exclude<ExportCellValue, null> =>
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean' ||
  (value instanceof Date && !Number.isNaN(value.getTime()));

const stringifyComplexValue = (value: unknown): string => {
  const normalize = (nestedValue: unknown, ancestors: WeakSet<object>): unknown => {
    if (typeof nestedValue === 'bigint') {
      return nestedValue.toString();
    }

    if (!nestedValue || typeof nestedValue !== 'object') {
      return nestedValue;
    }

    if (ancestors.has(nestedValue)) {
      return '[Circular]';
    }

    ancestors.add(nestedValue);
    const normalizedValue = Array.isArray(nestedValue)
      ? nestedValue.map((item) => normalize(item, ancestors))
      : Object.fromEntries(
          Object.entries(nestedValue as Record<string, unknown>)
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([key, item]) => [key, normalize(item, ancestors)])
        );
    ancestors.delete(nestedValue);

    return normalizedValue;
  };

  try {
    const serializedValue = JSON.stringify(normalize(value, new WeakSet()));
    return serializedValue ?? String(value);
  } catch {
    return String(value) === '[object Object]' ? '' : String(value);
  }
};

const getExportCellValue = ({ formattedValue, value }: GridCellParams): ExportCellValue => {
  if (isExportCellValue(formattedValue)) {
    return formattedValue;
  }

  if (isExportCellValue(value)) {
    return value;
  }

  if (value === null || value === undefined) {
    return null;
  }

  return stringifyComplexValue(value);
};

const toExcelCell = (value: ExportCellValue): Cell => {
  if (typeof value === 'string') {
    return { type: String, value };
  }

  return value;
};

const toSheetData = ({ headers, rows }: ExcelExportData): SheetData => [
  headers.map((header) => ({ type: String, value: header })),
  ...rows.map((row) => row.map(toExcelCell)),
];

export const exportToExcel = async (data: ExcelExportData): Promise<void> => {
  const { default: writeExcelFile } = await import('write-excel-file/browser');
  const blob = await writeExcelFile(toSheetData(data), {
    sheet: EXCEL_SHEET_NAME,
    dateFormat: EXCEL_DATE_FORMAT,
  }).toBlob();

  saveAs(blob, EXCEL_FILE_NAME);
};

export const getExportData = (
  apiRef: MutableRefObject<GridApi>,
  { allColumns = false }: { allColumns?: boolean } = {}
): ExcelExportData => {
  const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);
  const exportColumnFields = (
    allColumns ? gridColumnFieldsSelector(apiRef) : gridVisibleColumnFieldsSelector(apiRef)
  ).filter(
    (columnField) => !EXCLUDED_COLUMN_FIELDS.has(columnField) && !apiRef.current.getColumn(columnField)?.disableExport
  );

  return {
    headers: exportColumnFields.map(
      (columnField) => apiRef.current.getColumn(columnField)?.headerName || camelCaseToTitleCase(columnField)
    ),
    rows: filteredSortedRowIds.map((rowId) =>
      exportColumnFields.map((columnField) => getExportCellValue(apiRef.current.getCellParams(rowId, columnField)))
    ),
  };
};

export const exportStyles = (theme: Theme, showSearch: boolean, showCustomizeTable?: boolean) => {
  const defaultLeftPosition = showSearch || showCustomizeTable ? '278px' : '75px';
  const leftPosition = showCustomizeTable && showSearch ? '330px' : defaultLeftPosition;

  return {
    paddingLeft: 0,
    position: 'absolute',
    top: '-80px',
    left: leftPosition,
    background: 'transparent',
    color: 'transparent',
    ':hover': {
      background: 'inherit',
    },
    [theme.breakpoints.down('sm')]: {
      left: showCustomizeTable && showSearch ? '122px' : '66px',
      top: '-60px',
    },
  };
};
